import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './PaymentReleases.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PaymentReleases = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);

  const getToken = () => localStorage.getItem('marilink_token');

  const fetchPayments = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPayments(data.payments);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleRelease = async () => {
    if (!confirmRelease) {
      alert('Please confirm the release');
      return;
    }
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/payments/${selectedPayment._id}/release`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        alert('Payment released successfully');
        setShowReleaseModal(false);
        fetchPayments();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error releasing payment: ' + error.message);
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          payment.vesselName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    pending: payments.filter(p => p.status === 'pending').length,
    held: payments.filter(p => p.status === 'held').length,
    released: payments.filter(p => p.status === 'released').length,
    disputed: payments.filter(p => p.status === 'disputed').length
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'pending': return 'pending';
      case 'held': return 'held';
      case 'released': return 'released';
      case 'disputed': return 'disputed';
      default: return 'pending';
    }
  };

  if (loading) return <div className="loading-spinner">Loading payments...</div>;

  return (
    <div className="payment-releases-premium">
      <div className="page-header">
        <h1><i className="fas fa-hand-holding-usd"></i> Payment Releases</h1>
        <p>Manage and release escrow payments to vendors</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-clock"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending Approval</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-lock"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.held}</div>
            <div className="stat-label">Held in Escrow</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.released}</div>
            <div className="stat-label">Released</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-gavel"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.disputed}</div>
            <div className="stat-label">Disputed</div>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <button className={`filter-pill ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
          <button className={`filter-pill ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>Pending</button>
          <button className={`filter-pill ${filterStatus === 'held' ? 'active' : ''}`} onClick={() => setFilterStatus('held')}>Held</button>
          <button className={`filter-pill ${filterStatus === 'released' ? 'active' : ''}`} onClick={() => setFilterStatus('released')}>Released</button>
          <button className={`filter-pill ${filterStatus === 'disputed' ? 'active' : ''}`} onClick={() => setFilterStatus('disputed')}>Disputed</button>
        </div>
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Search payments..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="payments-list">
        {filteredPayments.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No payments found</p>
          </div>
        ) : (
          filteredPayments.map(payment => (
            <div key={payment._id} className="payment-card">
              <div className="payment-card-header">
                <div className="payment-info">
                  <span className="payment-id">#{payment._id.slice(-8)}</span>
                  <span className={`status-badge ${getStatusClass(payment.status)}`}>{payment.status}</span>
                </div>
                <div className="payment-amount">${payment.amount?.toLocaleString()}</div>
              </div>
              <div className="payment-card-body">
                <div className="detail-row">
                  <span className="label">Vessel:</span>
                  <span className="value">{payment.vesselName}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Vendor:</span>
                  <span className="value">{payment.vendorName}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Milestone:</span>
                  <span className="value">{payment.milestone || 'Final Payment'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Due Date:</span>
                  <span className="value">{new Date(payment.dueDate).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="payment-card-footer">
                <Button variant="secondary" size="small" onClick={() => navigate(`/admin/escrow/${payment._id}`)} icon="fa-eye">
                  Details
                </Button>
                {(payment.status === 'held' || payment.status === 'pending') && (
                  <Button variant="primary" size="small" onClick={() => { setSelectedPayment(payment); setShowReleaseModal(true); }} icon="fa-hand-holding-usd">
                    Release Payment
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Modal isOpen={showReleaseModal} onClose={() => setShowReleaseModal(false)} title="Release Payment">
        {selectedPayment && (
          <div className="release-modal-content">
            <div className="payment-summary">
              <p><strong>Vessel:</strong> {selectedPayment.vesselName}</p>
              <p><strong>Vendor:</strong> {selectedPayment.vendorName}</p>
              <p><strong>Amount:</strong> ${selectedPayment.amount?.toLocaleString()}</p>
              <p><strong>Milestone:</strong> {selectedPayment.milestone || 'Final Payment'}</p>
            </div>
            <div className="release-warning">
              <i className="fas fa-exclamation-triangle"></i>
              <p>This action will release funds to the vendor. This cannot be undone.</p>
            </div>
            <label className="confirm-checkbox">
              <input type="checkbox" checked={confirmRelease} onChange={(e) => setConfirmRelease(e.target.checked)} />
              <span>I confirm that all work has been verified and approved</span>
            </label>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowReleaseModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleRelease} disabled={!confirmRelease}>Release Payment</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PaymentReleases;